import { Icon, IconDefinitions } from '../../../../components';
import { HostInfo } from '../../../../data/entities';
import { getRelativeTime } from '../../../../app/dateHelpers';
import { formatBytes, formatUptime } from '../../helper';

const InfoTile = ({
  label,
  children,
  accentColor = 'var(--bs-primary)',
}: {
  label: string;
  children: React.ReactNode;
  accentColor?: string;
}) => (
  <div
    className="p-3 bg-light rounded h-100"
    style={{ borderLeft: `3px solid ${accentColor}` }}
  >
    <div className="text-muted small mb-1">{label}</div>
    <div className="fw-semibold">{children}</div>
  </div>
);

interface SystemInformationCardProps {
  hostInfo: HostInfo | null | undefined;
  heartbeatDateTime: string | null;
  isOnline: boolean;
}

export function SystemInformationCard({
  hostInfo,
  heartbeatDateTime,
  isOnline,
}: SystemInformationCardProps) {

  if (!hostInfo) {
    return (
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-4">
          <h5 className="mb-4 d-flex align-items-center">
            <Icon iconName={IconDefinitions.computer} className="me-2 text-primary" />
            System Information
          </h5>
          <div className="text-center text-muted py-4">
            <Icon iconName={IconDefinitions.computer} style={{ fontSize: '2.5rem' }} className="mb-3 opacity-25" />
            {isOnline ? (
              <>
                <p className="mb-0">Waiting for system data...</p>
                <small>This will populate once the device sends its first heartbeat.</small>
              </>
            ) : (
              <>
                <p className="mb-0">No system data available.</p>
                <small>The device is offline and has not sent any data yet.</small>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const usedMemory = hostInfo.totalMemoryBytes - hostInfo.availableMemoryBytes;
  const memoryPercent =
    hostInfo.totalMemoryBytes > 0
      ? Math.round((usedMemory / hostInfo.totalMemoryBytes) * 100)
      : 0;

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body p-4">
        <h5 className="mb-3 d-flex align-items-center">
          <Icon
            iconName={IconDefinitions.computer}
            className="me-2 text-primary"
          />
          System Information
          {heartbeatDateTime && (
            <small className="text-muted ms-auto fw-normal fs-6">
              as of {getRelativeTime(new Date(heartbeatDateTime))}
            </small>
          )}
        </h5>

        {!isOnline && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-4 py-2" role="alert">
            <Icon iconName={IconDefinitions.warning} className="mt-1 flex-shrink-0" />
            <div>
              <strong>Device is offline.</strong> Data shown is from the device last update
              {heartbeatDateTime && (
                <> on {new Date(heartbeatDateTime).toLocaleString()}</>
              )} and may no longer be accurate.
            </div>
          </div>
        )}

        {/* Machine + OS details */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <InfoTile label="Machine Name">{hostInfo.machineName}</InfoTile>
          </div>
          <div className="col-md-6">
            <InfoTile label="Logged In User">{hostInfo.userName}</InfoTile>
          </div>
          <div className="col-md-6">
            <InfoTile label="Operating System">
              {hostInfo.osVersion}
              <span className="ms-2 text-muted fw-normal small">
                {hostInfo.osArchitecture}{' '}
                {hostInfo.is64BitOperatingSystem ? '64-bit' : '32-bit'}
              </span>
            </InfoTile>
          </div>
          <div className="col-md-6">
            <InfoTile label="Runtime">
              {hostInfo.runtimeVersion}
              <span className="ms-2 text-muted fw-normal small">
                {hostInfo.processArchitecture}
              </span>
            </InfoTile>
          </div>
          <div className="col-md-6">
            <InfoTile label="CPU Cores">
              {hostInfo.processorCount} logical cores
            </InfoTile>
          </div>
          <div className="col-md-6">
            <InfoTile label="System Uptime">
              {formatUptime(hostInfo.systemUptimeMilliseconds)}
            </InfoTile>
          </div>
        </div>

        {/* Memory & Drives */}
        <div className="row g-3">
          {/* Memory */}
          <div className={hostInfo.drives?.length > 0 ? 'col-md-6' : 'col-12'}>
            <h6 className="text-muted mb-3">
              <Icon iconName={IconDefinitions.chartLine} className="me-1" />
              Memory
            </h6>
            <div className="p-3 bg-light rounded">
              <div className="d-flex justify-content-between mb-2">
                <span className="small fw-medium">
                  {formatBytes(usedMemory)} used of{' '}
                  {formatBytes(hostInfo.totalMemoryBytes)}
                </span>
                <span
                  className={`small fw-semibold ${
                    memoryPercent > 85
                      ? 'text-danger'
                      : memoryPercent > 65
                      ? 'text-warning'
                      : 'text-success'
                  }`}
                >
                  {memoryPercent}%
                </span>
              </div>
              <div
                className="progress rounded-pill"
                style={{ height: '12px' }}
              >
                <div
                  className={`progress-bar rounded-pill ${
                    memoryPercent > 85
                      ? 'bg-danger'
                      : memoryPercent > 65
                      ? 'bg-warning'
                      : 'bg-success'
                  }`}
                  style={{
                    width: `${memoryPercent}%`,
                    transition: 'width 0.6s ease',
                  }}
                />
              </div>
              <div className="text-muted small mt-2">
                {formatBytes(hostInfo.availableMemoryBytes)} available
              </div>
            </div>
          </div>

          {/* Drives */}
          {hostInfo.drives?.length > 0 && (
            <div className="col-md-6">
              <h6 className="text-muted mb-3">
                <Icon iconName={IconDefinitions.computer} className="me-1" />
                Drives
              </h6>
              <div className="row g-3">
                {hostInfo.drives.map((drive) => {
                  const driveUsed = drive.totalSize - drive.freeSpace;
                  const drivePercent =
                    drive.totalSize > 0
                      ? Math.round((driveUsed / drive.totalSize) * 100)
                      : 0;
                  return (
                    <div key={drive.name} className="col-12">
                      <div className="p-3 bg-light rounded">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>{drive.name}</strong>
                          <span
                            className={`small fw-semibold ${
                              drivePercent > 90
                                ? 'text-danger'
                                : drivePercent > 75
                                ? 'text-warning'
                                : 'text-primary'
                            }`}
                          >
                            {drivePercent}%
                          </span>
                        </div>
                        <div
                          className="progress rounded-pill mb-2"
                          style={{ height: '10px' }}
                        >
                          <div
                            className={`progress-bar rounded-pill ${
                              drivePercent > 90
                                ? 'bg-danger'
                                : drivePercent > 75
                                ? 'bg-warning'
                                : 'bg-primary'
                            }`}
                            style={{
                              width: `${drivePercent}%`,
                              transition: 'width 0.6s ease',
                            }}
                          />
                        </div>
                        <div className="text-muted small">
                          {formatBytes(drive.freeSpace)} free of{' '}
                          {formatBytes(drive.totalSize)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}